import { computed, onUnmounted, watch } from 'vue';
import { storeToRefs } from 'pinia';

import {
  ensureHasAccess,
  onStaleDataRefetchKey,
  refetchKeys,
  useAccessControlStore,
} from '@peanuds/app-shell';
import { getIsoDateDaysAgo } from '@peanuds/shared';
import {
  DEFAULT_LIMIT,
  useAccountSelection,
  usePaginatedTransactionsFeed,
  useUserAccountsStore,
  type TransactionDirection,
} from '@peanuds/shared-state';

import type { ControlSpendingsTableFilter } from '../internal/control-spendings-table.types';

const CONTROL_SPENDINGS_RECENT_DAYS = 14;
export const CONTROL_SPENDINGS_PAGE_SIZE = 20;

export const getControlSpendingDirections = (
  filter: ControlSpendingsTableFilter,
): readonly TransactionDirection[] => {
  if (filter === 'incoming') {
    return ['in'];
  }

  if (filter === 'outgoing') {
    return ['out'];
  }

  return ['in', 'out'];
};

type UseControlSpendingsTableFeedOptions = {
  filter: () => ControlSpendingsTableFilter;
};

export const useControlSpendingsTableFeed = ({ filter }: UseControlSpendingsTableFeedOptions) => {
  const { resolvedTransactionAccountIds, resolvedTransactionAccountIdsKey } = useAccountSelection();
  const userAccountsStore = useUserAccountsStore();
  const accessControlStore = useAccessControlStore();
  const {
    isLoading: isAccountsLoading,
    hasLoadedAccounts,
    error: accountsError,
  } = storeToRefs(userAccountsStore);
  const { isAccessResolved, isOnboarded } = storeToRefs(accessControlStore);

  const selectedDirections = computed(() => getControlSpendingDirections(filter()));
  const isResolvingTransactionAccounts = computed(() => {
    if (!isAccessResolved.value) return true;
    if (!isOnboarded.value) return false;
    if (resolvedTransactionAccountIdsKey.value.length > 0) return false;
    if (accountsError.value !== undefined) return false;
    if (!hasLoadedAccounts.value) return true;

    return isAccountsLoading.value;
  });

  const transferDateFrom = getIsoDateDaysAgo(CONTROL_SPENDINGS_RECENT_DAYS);
  const transactionsFeed = usePaginatedTransactionsFeed({
    accountIds: resolvedTransactionAccountIds,
    directions: selectedDirections,
    transferDateFrom,
    includeGenericTransactions: true,
    pageSize: CONTROL_SPENDINGS_PAGE_SIZE,
    sourcePageSize: DEFAULT_LIMIT,
  });

  const canLoadTransactions = () => {
    return (
      isOnboarded.value && resolvedTransactionAccountIdsKey.value.length > 0 && ensureHasAccess()
    );
  };

  watch(
    () =>
      [
        isAccessResolved.value,
        isOnboarded.value,
        resolvedTransactionAccountIdsKey.value,
        filter(),
      ] as const,
    async () => {
      if (!canLoadTransactions()) {
        transactionsFeed.reset();
        return;
      }

      await transactionsFeed.loadInitial();
    },
    { immediate: true },
  );

  const refreshTransactionsFromStaleData = () => {
    if (!canLoadTransactions()) {
      return;
    }

    void transactionsFeed.loadInitial();
  };

  const staleRefetchKeys = [
    refetchKeys.INCOMING_TRANSFER,
    refetchKeys.OUTGOING_TRANSFERS,
    refetchKeys.GENERIC_TRANSACTION,
    refetchKeys.EXCHANGE,
  ] as const;

  const unregisterStaleDataListeners = staleRefetchKeys.map((refetchKey) => {
    return onStaleDataRefetchKey(refetchKey, refreshTransactionsFromStaleData);
  });

  onUnmounted(() => {
    for (const unregister of unregisterStaleDataListeners) {
      unregister();
    }
  });

  return {
    pageSize: CONTROL_SPENDINGS_PAGE_SIZE,
    transferDateFrom,
    resolvedTransactionAccountIds,
    transactions: transactionsFeed.rows,
    isTransactionsLoading: computed(
      () => isResolvingTransactionAccounts.value || transactionsFeed.isLoading.value,
    ),
    transactionsError: transactionsFeed.error,
    hasMore: transactionsFeed.hasMore,
    loadMore: transactionsFeed.loadMore,
  };
};
